import USBButton from "@usb-shield/react-button";
import USBLink from "@usb-shield/react-link";
import { Separator } from "@repo/ui/components/ui/separator";
import type { HeroSectionProps } from "../../../smb-ui-welcome/src/types/components/core.type";
import parse from "html-react-parser";
import { memo, useMemo } from "react";
import clsx from "clsx"
import { ELAVON_ACCESS_LABEL, NEED_HELP_LABEL, FORGOT_PASS_LABEL, RESEND_REG_LABEL } from "#constants/index.ts";

const sectionClass = clsx("bg-gradient-to-br from-[#F3C0FF4D] from-10% via-[#F8FBFF] via-30% to-[#B9CDFF4D]", "h-[410px]", "border-t", "border-gray-300");
const containerClass = clsx("container", "relative", "flex", "justify-between", "h-full", "p-4", "lg:p-0", "lg:py-4", "lg:px-[6.25%]");

const { VITE_ELAVON_ACCESS_URL, VITE_AEM_BASE_URL, VITE_FORGOT_PASS_URL, VITE_RESEND_REG_MAIL, } = window.__conf__ ?? {};

const HeroSection = memo(({
    aemContent = {
        ctaLabel: "",
        image1: "",
        image1_alt: "",
        ribbonHeading: "",
        ribbonHeadingBodyCopy: "",
        primaryButton: {
            label: "",
            url: ""
        },
        secondaryButton: {
            label: "",
            url: "",
            target: ""
        }
    }
}: HeroSectionProps) => {
    // Destructure aemContent for better readability
    const {
        ribbonHeading,
        ribbonHeadingBodyCopy,
        image1,
        image1_alt,
    } = aemContent;

    // Memoize parsed HTML content
    const parsedContent = useMemo(() => ({
        heading: parse(ribbonHeading),
        bodyCopy: parse(ribbonHeadingBodyCopy),
    }), [ribbonHeading, ribbonHeadingBodyCopy]);

    const handleLoginClick = () => {
        if (VITE_ELAVON_ACCESS_URL) {
            window.location.href = VITE_ELAVON_ACCESS_URL
        }
    }

    return (
        <section
            data-testid="hero-section"
            className={sectionClass}
        >
            {/* <div className="absolute inset-0 bg-gradient-to-br from-[#F3C0FF4D] from-10% to-transparent" /> */}
            <div className={containerClass}>
                <div className="flex flex-col justify-center gap-y-2 lg:max-w-[calc(100%-28rem)]">
                    <h1 className="flex flex-col gap-2 text-3xl [&_p]:font-medium text-[#0C2074] lg:text-5xl">
                        {parsedContent?.heading}
                    </h1>
                    <div className="text-[20px] text-[#1E1E22] text-foreground w-full md:w-[380px] lg:w-[480px] pt-4">
                        {parsedContent?.bodyCopy}
                    </div>
                    <div className="pt-7">
                        <USBButton handleClick={handleLoginClick} addClasses="min-w-[109px] w-full md:w-fit">{ELAVON_ACCESS_LABEL}</USBButton>
                    </div>
                    <div className="text-[#282828] pt-7 font-medium text-normal">{NEED_HELP_LABEL}</div>
                    <div className="flex gap-x-[10px] md:justify-start md:gap-x-4">
                        <USBLink href={VITE_FORGOT_PASS_URL}>{FORGOT_PASS_LABEL}</USBLink>
                        <Separator role="separator" className="w-[1px] h-full bg-[#235AE4]" />
                        <USBLink href={VITE_RESEND_REG_MAIL}>{RESEND_REG_LABEL}</USBLink>
                    </div>
                </div>
                {image1 && (
                    <div className="lg:absolute right-[3.25%] hidden md:flex top-6">
                        <img
                            data-testid="banner-image"
                            src={`${VITE_AEM_BASE_URL}${image1}`}
                            srcSet={`${VITE_AEM_BASE_URL}${image1}?w=400 400w, ${VITE_AEM_BASE_URL}${image1}?w=800 800w`}
                            alt={image1_alt}
                            className="mt-0 w-[28rem] h-[28rem] object-contain"
                            loading="lazy"
                        />
                    </div>
                )}
            </div>
        </section>
    );
});

export default HeroSection;